import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { groupBy } from "lodash";
import { ResponsiveBar } from "@nivo/bar";

import InformationalTooltip from "../InformationalTooltip";

const colors = ["#92949d", "#ffce3f", "#50accb"];

const ActiveProjectsByTeam = ({ calls, dark }) => {
  const groupedByDepartmentCategory = useMemo(
    () =>
      groupBy(
        calls,
        (call) =>
          (call.project.department_category &&
            call.project.department_category.name) ||
          undefined
      ),
    [calls]
  );

  const result = Object.keys(groupedByDepartmentCategory)
    .map((key) => {
      const categories = [
        {
          name: "Internal",
          color: "#92949d",
          actualHrs: 0,
          projects: []
        },
        { name: "Support", color: "#ffce3f", actualHrs: 0, projects: [] },
        { name: "Project", color: "#50accb", actualHrs: 0, projects: [] }
      ];
      const projects = [];
      let actualHours = 0;
      groupedByDepartmentCategory[key].forEach((call) => {
        const projectCategory = call.project.category?.label;
        const categoryIndex = categories.findIndex(
          (cat) => cat.name === projectCategory
        );
        if (categoryIndex > -1 && call.actual_hrs > 0) {
          categories[categoryIndex].actualHrs += call.actual_hrs;
          actualHours += call.actual_hrs;
          if (!categories[categoryIndex].projects.includes(call.project._id)) {
            categories[categoryIndex].projects.push(call.project._id);
            projects.push(call.project._id);
          }
        }
      });
      return {
        departmentCategory: key !== "undefined" ? key : "N/A",
        data: categories,
        projects,
        actualHours
      };
    })
    .filter((c) => c.actualHours > 0);

  const transformedResult = result
    .map((item) => {
      const newData = item.data.reduce((acc, entry) => {
        acc[entry.name] = entry.actualHrs;
        return acc;
      }, {});

      return {
        departmentCategory:
          item.departmentCategory.length <= 15
            ? item.departmentCategory
            : `${item.departmentCategory.slice(0, 15)}...`,
        ...newData
      };
    })
    .sort((a, b) => {
      const nameA = a.departmentCategory.toUpperCase();
      const nameB = b.departmentCategory.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    })
    .reverse();

  return (
    <React.Fragment>
      {result.length > 0 && (
        <div className="content-wrapper active-projects-container">
          <div className="header-section-container">
            <h1>Project Breakdown</h1>
            <InformationalTooltip
              id="active_projects"
              dark={dark}
              content="Count of projects by department category"
            />
          </div>
          {/* We need this because current version of nivo doesn't seem to accept props to change legends text color */}
          <div
            style={{
              width: "290px",
              height: "35px",
              position: "absolute",
              bottom: "17px",
              background: "white",
              left: "120px"
            }}
          />
          <div
            style={{
              height:
                50 * transformedResult.length > 150
                  ? 50 * transformedResult.length
                  : 150
            }}
          >
            <ResponsiveBar
              data={transformedResult}
              keys={["Internal", "Support", "Project"]}
              indexBy="departmentCategory"
              layout="horizontal"
              colors={colors}
              margin={{ top: 20, right: 60, bottom: 70, left: 100 }}
              labelSkipWidth={10}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -15,
                truncateTickAt: 15
              }}
              legends={[
                {
                  dataFrom: "keys",
                  anchor: "bottom-left",
                  direction: "row",
                  justify: false,
                  translateX: 0,
                  translateY: 60,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemsSpacing: 2,
                  symbolSize: 20,
                  itemDirection: "left-to-right"
                }
              ]}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fill: dark ? "white" : "black"
                    }
                  },
                  legend: {
                    text: {
                      fill: dark ? "white" : "black"
                    }
                  }
                },
                tooltip: {
                  container: {
                    background: dark ? "black" : "white",
                    color: dark ? "white" : "black"
                  }
                }
              }}
              tooltipFormat={(value) => `${value} hours`}
            />
          </div>
        </div>
      )}
      {result.length === 0 && (
        <div className="no-active-projects-container">
          <h4>No Data To Show</h4>
        </div>
      )}
    </React.Fragment>
  );
};

ActiveProjectsByTeam.propTypes = {
  calls: PropTypes.array,
  dark: PropTypes.bool
};

ActiveProjectsByTeam.defaultProps = {
  calls: [],
  dark: false
};

export default ActiveProjectsByTeam;
